import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";

import Button from "../../../components/Button";
import NavigationRail from "../../../components/v2/Admin/NavigationRail";
import Snackbar from "../../../components/v2/Snackbar";
import AuthService, { UserProfile, isAuthenticated } from "../../../services/AuthService";
import ErrorPage from "../Error";
import AccountMenu from "./AccountMenu";
import AdminAttendants from "./Attendants";
import AdminHome from "./Home";
import AdminNotices from "./Notices";
import PasswordDialog from "./PasswordDialog";
import AdminPlaceholder from "./Placeholder";
import AdminTransparency from "./Transparency";
import AdminUsers from "./Users";
import { ADMIN_NAVIGATION, can, isActivePath } from "./navigation";

import style from "./styles.module.scss";

const keyI18n = "translations.admin";
const COMPACT_BREAKPOINT = 840;

const SCREENS: { [key: string]: { component: React.ComponentType; subpath: string } | undefined } = {
  transparency: { component: AdminTransparency, subpath: "/:id?" },
  notices: { component: AdminNotices, subpath: "" },
  users: { component: AdminUsers, subpath: "" },
  attendants: { component: AdminAttendants, subpath: "" },
};

type Status = "loading" | "ready" | "forbidden" | "unavailable";

export default function AdminArea() {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();

  const authenticated = isAuthenticated();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [leaving, setLeaving] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const closeMessage = useCallback(() => setMessage(null), []);

  useEffect(() => {
    if (!authenticated) {
      return;
    }

    AuthService.adminProfile()
      .then((data) => {
        setProfile(data);
        setStatus("ready");
      })
      .catch((error) => setStatus(isForbidden(error) ? "forbidden" : "unavailable"));
  }, [authenticated]);

  useEffect(() => {
    if (!navigationOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setNavigationOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= COMPACT_BREAKPOINT) {
        setNavigationOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = "";
    };
  }, [navigationOpen]);

  if (!authenticated) {
    return <Redirect to={pathname.replace(/\/+$/, "") === "/admin" ? "/admin/login" : "/404"} />;
  }

  if (status === "forbidden") {
    return <ErrorPage code="403" homeLink="/" />;
  }

  document.title = `Portal da PGM - ${t(`${keyI18n}.title`)}`;

  const handleLogout = async () => {
    setLeaving(true);
    await AuthService.logout().catch(() => undefined);
    history.replace("/admin/login");
  };

  const activities = profile ? profile.activities : [];
  const displayName = profile ? profile.name || profile.email : "";
  const currentItem = ADMIN_NAVIGATION.find((item) => isActivePath(pathname, item.path));
  const plainSurface = !!currentItem && !!SCREENS[currentItem.key] && can(activities, currentItem.activity);

  return (
    <div className={style.page}>
      <header className={style.topbar}>
        <div className={style.brand}>
          {profile && (
            <Button
              iconButton
              variant="text"
              shape="round"
              size="small"
              className={style.navigationToggle}
              aria-controls="admin-navigation"
              aria-expanded={navigationOpen}
              aria-label={t(`${keyI18n}.nav.open`)}
              onClick={() => setNavigationOpen(true)}
              icon={<span className="material-symbols-sharp">menu</span>}
            />
          )}
          <span aria-hidden="true" className={`material-symbols-sharp ${style.brandIcon}`}>
            admin_panel_settings
          </span>
          <span className="pgm-title-large">{t(`${keyI18n}.title`)}</span>
        </div>

        {profile && (
          <AccountMenu
            name={displayName}
            disabled={leaving}
            onChangePassword={() => setPasswordOpen(true)}
            onLogout={handleLogout}
          />
        )}
      </header>

      {status === "ready" && profile ? (
        <div className={style.body}>
          {navigationOpen && (
            <div
              aria-hidden="true"
              className={style.scrim}
              onClick={() => setNavigationOpen(false)}
            />
          )}

          <aside
            className={`${style.sidebar} ${navigationOpen ? style.sidebarOpen : ""}`}
            id="admin-navigation"
          >
            <div className={style.sidebarHeader}>
              <Button
                iconButton
                variant="text"
                shape="round"
                size="small"
                aria-label={t(`${keyI18n}.nav.close`)}
                onClick={() => setNavigationOpen(false)}
                icon={<span className="material-symbols-sharp">menu_open</span>}
              />
            </div>
            <NavigationRail activities={activities} onNavigate={() => setNavigationOpen(false)} />
          </aside>

          <main className={`${style.content} ${plainSurface ? style.contentPlain : ""}`}>
            <Switch>
              <Route exact path="/admin">
                <AdminHome name={displayName} />
              </Route>
              {ADMIN_NAVIGATION.map((item) => {
                const screen = SCREENS[item.key];
                const Screen = screen && screen.component;

                return (
                  <Route key={item.key} exact={!!screen} path={screen ? `${item.path}${screen.subpath}` : item.path}>
                    {!can(activities, item.activity) ? (
                      <ErrorPage code="403" homeLink="/admin" embedded />
                    ) : Screen ? (
                      <Screen />
                    ) : (
                      <AdminPlaceholder screen={t(`${keyI18n}.nav.items.${item.key}`)} />
                    )}
                  </Route>
                );
              })}
              <Route path="*">
                <Redirect to="/404" />
              </Route>
            </Switch>
          </main>
        </div>
      ) : (
        <p className={`pgm-body-large ${style.status}`} role="status">
          {status === "unavailable" ? t(`${keyI18n}.login.unavailable`) : t(`${keyI18n}.home.loading`)}
        </p>
      )}

      <PasswordDialog
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        onChanged={() => {
          setPasswordOpen(false);
          setMessage(t(`${keyI18n}.account.password.changed`));
        }}
      />
      <Snackbar message={message} onClose={closeMessage} />
    </div>
  );
}

function isForbidden(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { error?: string }).error === "forbidden";
}
